import { ApolloLink, Operation } from '@apollo/client';
import { OperationDefinitionNode } from 'graphql';

import { GET_USER, GetUserQuery, GetUserQueryVariables } from '@neo.tax/shared/graphql';
import { sendFSEvent } from '@neo.tax/shared/third-parties';
import { pascalToSnakeCase } from '@neo.tax/shared/utils';

import { apolloClient } from '../client';

interface FsEvent {
  eventName: string;
  additionalData?: Record<string, unknown>;
}

const eligibilityAdditionalData = (operation: Operation): Record<string, string> => ({
  // Note: qualify if the last question is answered.
  qualified: typeof operation.variables.hasFiledRDTaxCreditBefore === 'boolean' ? 'qualify' : 'disqualify',
});

const events: Record<string, FsEvent | ((operation: Operation) => FsEvent)> = {
  CreateCompanyWithEligibility: (operation) => ({
    eventName: 'create_company_with_eligibility',
    additionalData: eligibilityAdditionalData(operation),
  }),
  SubmitClientEligibility: (operation) => ({
    eventName: 'submit_client_eligibility',
    additionalData: eligibilityAdditionalData(operation),
  }),
  Eligibility: (operation) => ({
    eventName: 'submit_eligibility',
    additionalData: eligibilityAdditionalData(operation),
  }),
  MarkWelcomeMessageAsViewed: { eventName: 'mark_welcome_message_as_viewed' },
  ProvisionEmailPasswordUser: { eventName: 'create_account' },
  SyncAccountingProvider: {
    eventName: 'sync_accounting_provider',
    additionalData: { sync_method: 'link' },
  },
  SyncPayrollProvider: {
    eventName: 'sync_payroll_provider',
    additionalData: { sync_method: 'link' },
  },
  UploadEmployeePayroll: {
    eventName: 'sync_payroll_provider',
    additionalData: { sync_method: 'upload' },
  },
  ComputeRDTaxCredit: { eventName: 'calculate_credit' },
  ResetRdTaxCredit: { eventName: 'reset_rd_tax_credit' },
  ValidateStripePromoCode: { eventName: 'apply_promo_code' },
  AssignStripeCheckoutSession: { eventName: 'save_payment_info' },
  UpdatePayment: { eventName: 'update_payment_info' },
  Generate6765Pdf: { eventName: 'generate_6765' },
  CreateNewCompany: { eventName: 'add_client' },
  MarkExpenseAsCompleted: (operation) => ({
    eventName: 'mark_expense_tab',
    additionalData: { tab: operation.variables.expense },
  }),
};

export const analyticsLink = new ApolloLink((operation, forward) =>
  forward(operation).map((data) => {
    const { operationName, query } = operation;
    const operationDefinition = query.definitions[0] as OperationDefinitionNode;
    if (
      operationName &&
      ((operationDefinition && operationDefinition.operation === 'mutation') || events[operationName])
    ) {
      const operationEvent = events[operationName] || {
        eventName: pascalToSnakeCase(operationName),
      };
      const event = operationEvent instanceof Function ? operationEvent(operation) : operationEvent;
      const { getUser = null } = apolloClient.readQuery<GetUserQuery, GetUserQueryVariables>({ query: GET_USER }) || {};

      sendFSEvent(
        event.eventName,
        {
          ...(event.additionalData || {}),
          result: data.errors ? 'error' : 'success',
        },
        getUser,
        null,
      );
    }
    return data;
  }),
);
